import { useEffect, useState, useCallback } from "react";
import { Reply, Archive, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { get, put, post, inputCls, btnDark, btnLight, Card, fmtDate, Empty } from "./shared";

const MSG_STATUS = { new: { label: "Nouveau", cls: "bg-amber-50 text-amber-800 border-amber-200" }, read: { label: "Lu", cls: "bg-stone-100 text-stone-600 border-stone-200" }, replied: { label: "Répondu", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" }, archived: { label: "Archivé", cls: "bg-stone-50 text-stone-400 border-stone-200" } };

const Message = ({ m, onChanged }) => {
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const st = MSG_STATUS[m.status || "new"];

  const setStatus = async (status) => {
    try { await put(`/messages/${m.id}`, { status }); onChanged(); } catch { toast.error("Erreur"); }
  };
  const send = async () => {
    setBusy(true);
    try {
      await post(`/messages/${m.id}/reply`, { body: reply });
      toast.success(`Réponse envoyée à ${m.email}`);
      setOpen(false); setReply(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Card className={`p-5 ${m.status === "archived" ? "opacity-60" : ""}`} data-testid={`admin-message-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm text-stone-900 font-medium">{m.name} · <a href={`mailto:${m.email}`} className="font-mono text-xs text-stone-500 hover:text-[#C85A32]">{m.email}</a></p>
          <p className="mt-1 text-[10px] font-mono uppercase tracking-wide text-[#C85A32]">{m.subject}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase ${st.cls}`} data-testid={`message-status-${m.id}`}>{st.label}</span>
          <span className="text-xs font-mono text-stone-400">{fmtDate(m.created_at, true)}</span>
        </div>
      </div>
      <p className="mt-3 text-sm text-stone-700 leading-relaxed whitespace-pre-line">{m.message}</p>
      {m.reply && (
        <div className="mt-4 border-l-2 border-[#4A624E] pl-4">
          <p className="text-[10px] font-mono uppercase tracking-wide text-[#4A624E]">Votre réponse · {fmtDate(m.replied_at, true)}</p>
          <p className="mt-1 text-sm text-stone-600 whitespace-pre-line">{m.reply}</p>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {!open && m.status !== "archived" && <button data-testid={`message-reply-${m.id}`} onClick={() => { setOpen(true); if (!m.status || m.status === "new") setStatus("read"); }} className={btnDark}><Reply size={13} /> Répondre par email</button>}
        {(!m.status || m.status === "new") && <button data-testid={`message-read-${m.id}`} onClick={() => setStatus("read")} className={btnLight}><MailOpen size={13} /> Marquer lu</button>}
        {m.status !== "archived" ? <button data-testid={`message-archive-${m.id}`} onClick={() => setStatus("archived")} className={btnLight}><Archive size={13} /> Archiver</button>
          : <button onClick={() => setStatus("read")} className={btnLight}>Désarchiver</button>}
      </div>
      {open && (
        <div className="mt-4 space-y-3" data-testid={`message-reply-form-${m.id}`}>
          <textarea data-testid={`message-reply-text-${m.id}`} rows={5} value={reply} onChange={(e) => setReply(e.target.value)} className={`${inputCls} w-full`} placeholder={`Bonjour ${m.name},\n\n…`} />
          <div className="flex gap-2">
            <button data-testid={`message-reply-send-${m.id}`} onClick={send} disabled={busy || reply.trim().length < 2} className={btnDark}>{busy ? "Envoi…" : "Envoyer"}</button>
            <button onClick={() => setOpen(false)} className={btnLight}>Annuler</button>
          </div>
        </div>
      )}
    </Card>
  );
};

export const Messages = ({ onChanged }) => {
  const [msgs, setMsgs] = useState(null);
  const [filter, setFilter] = useState("active");
  const load = useCallback(() => get("/messages").then(setMsgs).catch(() => setMsgs([])), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged(); };
  const list = (msgs || []).filter((m) => filter === "all" ? true : filter === "active" ? m.status !== "archived" : (m.status || "new") === filter);
  return (
    <div className="space-y-4" data-testid="admin-messages">
      <div className="flex flex-wrap gap-2">
        {[["active", "Actifs"], ["new", "Nouveaux"], ["read", "Lus"], ["replied", "Répondus"], ["archived", "Archivés"], ["all", "Tous"]].map(([k, l]) => (
          <button key={k} data-testid={`messages-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-wide border transition-colors ${filter === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600"}`}>{l}</button>
        ))}
      </div>
      {msgs === null ? <Empty>Chargement…</Empty> : list.length === 0 ? <Empty>Aucun message.</Empty> : list.map((m) => <Message key={m.id} m={m} onChanged={changed} />)}
    </div>
  );
};
